// IndexedDB: the offline copy of recipes, lists and in-progress work.

import { Dexie, type EntityTable } from 'dexie';
import type { AiRecipe, RecipeDetail, RecipeSummary, UserList } from '@dishly/shared';

export interface CachedRecipe extends RecipeDetail {
  cachedAt: number;
}

/** Where the reader is up to in a recipe, kept per device. */
export interface CookingProgress {
  recipeId: string;
  /** Progress resets when the recipe is edited. */
  recipeUpdatedAt: string;
  currentStep: number;
  /** Ticked ingredient indexes per step index. */
  ticks: Record<number, number[]>;
  servings: number | null;
  updatedAt: number;
}

/** A list change made locally and not yet confirmed by the server. */
export interface OutboxEntry {
  seq?: number;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  createdAt: number;
}

/** Unsaved editor state, so work survives a reload or going offline. */
export interface LocalDraft {
  key: string;
  form: unknown;
  savedAt: number;
}

/** A recipe as changed by the AI for this reader ("I don't have Worcestershire sauce"). */
export interface RecipeAdaptation {
  recipeId: string;
  /** The version it was made from; dropped when the recipe is edited. */
  recipeUpdatedAt: string;
  recipe: AiRecipe;
  /** Every change so far, oldest first. */
  changes: string[];
  updatedAt: number;
}

export interface ListTicks {
  key: string;
  ticked: string[];
}

export interface Meta {
  key: string;
  value: unknown;
}

export const db = new Dexie('dishly') as Dexie & {
  summaries: EntityTable<RecipeSummary, 'id'>;
  recipes: EntityTable<CachedRecipe, 'id'>;
  lists: EntityTable<UserList, 'id'>;
  outbox: EntityTable<OutboxEntry, 'seq'>;
  progress: EntityTable<CookingProgress, 'recipeId'>;
  drafts: EntityTable<LocalDraft, 'key'>;
  adaptations: EntityTable<RecipeAdaptation, 'recipeId'>;
  listTicks: EntityTable<ListTicks, 'key'>;
  meta: EntityTable<Meta, 'key'>;
};

db.version(1).stores({
  summaries: 'id',
  recipes: 'id',
  lists: 'id',
  outbox: '++seq',
  progress: 'recipeId',
  drafts: 'key',
  listTicks: 'key',
  meta: 'key',
});

db.version(2).stores({ adaptations: 'recipeId' });

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}
